import clsx from "clsx";
import type { ReactNode } from "react";

import { badgeColorClass } from "./badge-color";
import type { BadgeColor } from "./badge-color";

import styles from "./badge.module.css";

export type { BadgeColor, BadgeTone } from "./badge-color";

/**
 * `"pill"` (the default, `--pallet-role-radius-full`) is every colour-driven
 * badge SR-301 phase 8 measured — see the phase 8 doc §2b's "radius trap":
 * build on the token, never the `20px` literal the retired rules carried, or
 * this mismatches `ToggleChip`/`ActionChip`. `"rounded"`
 * (`--pallet-role-radius-6`) is the other geometry phase 8's grounding found
 * — `items.tsx`'s static, non-id `.pill`/`.pillAccent` — which sits outside
 * this unit's blast radius (`zones.tsx`/`user-groups.tsx`/`selectors.tsx`
 * only) and so is not migrated here. The shape axis is built anyway so the
 * primitive is complete for whichever unit does.
 */
export type BadgeShape = "pill" | "rounded";

/**
 * How the label is cased (SR-342 P4).
 *
 * An explicit axis rather than a blanket `text-transform: uppercase` on
 * `.badge`, because the two statuses SR-342 moves onto `Badge` disagree: the
 * PM asked for plan status in all caps ("NOT ACTIVATED", "ACTIVATED",
 * "COMPLETE") and named the task statuses in sentence case ("Open", "In
 * progress", "Complete"). `"sentence"` is the default for the same reason
 * `"pill"` is — it is what a caller that has not thought about it should get,
 * and it leaves the string the translator wrote alone.
 *
 * `text-transform`, not an `.toUpperCase()` on the label: the uppercasing is
 * presentation, so the accessible name and anything copied out of the page
 * stay as authored, and locales where casing is not a safe transform are the
 * browser's problem rather than ours.
 */
export type BadgeCase = "sentence" | "upper";

export interface BadgeProps {
  label: ReactNode;
  color: BadgeColor;
  shape?: BadgeShape;
  textCase?: BadgeCase;
  "data-test-role"?: string;
}

function Badge({
  label,
  color,
  shape = "pill",
  textCase = "sentence",
  "data-test-role": testRole,
}: BadgeProps) {
  return (
    <span
      className={clsx(styles.badge, badgeColorClass(color), {
        [styles.rounded]: shape === "rounded",
        [styles.upper]: textCase === "upper",
      })}
      data-test-role={testRole}
    >
      {label}
    </span>
  );
}

export default Badge;
